import { useState, useCallback, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'src/utils/axios';
import moment from 'moment';
// @mui
import {
  Grid,
  Typography,
  Box,
  Button,
  Link,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
// utils
import { fDateShort } from '../../../../utils/formatTime';
// hooks
import useIsMountedRef from '../../../../hooks/useIsMountedRef';
// components
import { DialogAnimate } from '../../../../components/animate';
import { SkeletonGraph } from '../../../../components/skeleton';
// @types
import { ReviewDueType } from 'src/@types/user';

// ----------------------------------------------------------------------

const initReviews: ReviewDueType[] = [];

type ModalState = {
  isOpenModal: boolean;
  reviews: ReviewDueType[];
};
const initialModalState: ModalState = {
  isOpenModal: false,
  reviews: initReviews,
};

// ----------------------------------------------------------------------

export default function UpcomingReviewsSection() {
  const isMountedRef = useIsMountedRef();
  const [modalState, setModalState] = useState(initialModalState);
  const [isLoading, setIsLoading] = useState(false);
  const [reviews30Day, setReviews30Day] = useState(initReviews);
  const [reviews90Day, setReviews90Day] = useState(initReviews);

  const getReviewDueData = useCallback(async () => {
    try {
      let url = `/app/shared/reviews/due`;
      let data = {};

      const response = await axios.get(url, {
        params: data,
      });

      if (isMountedRef.current) {
        let { data } = response.data;

        await setReviewDueData(data);
      }
    } catch (err) {
      console.log(err);
    }
  }, [isMountedRef]);

  const setReviewDueData = async (data: any) =>
    new Promise((resolve, reject) => {
      let tempReviews30Day: ReviewDueType[] = [];
      let tempReviews90Day: ReviewDueType[] = [];

      const compareNowDate = moment().format('YYYY-MM-DD');
      const compareDate30 = moment().add(30, 'day').format('YYYY-MM-DD');
      const compareDate90 = moment().add(90, 'day').format('YYYY-MM-DD');

      for (let i = 0; i < data.length; i++) {
        let curReview: ReviewDueType = data[i];

        if (!!!curReview.next_review_date || curReview.next_review_date < compareNowDate) {
          continue;
        } else if (curReview.next_review_date < compareDate30) {
          tempReviews30Day.push(curReview);
        } else if (curReview.next_review_date < compareDate90) {
          tempReviews90Day.push(curReview);
        }
      }

      setReviews30Day(tempReviews30Day);
      setReviews90Day(tempReviews90Day);
      resolve(null);
    });

  const init = useCallback(async () => {
    setIsLoading(true);

    await getReviewDueData();

    setIsLoading(false);
  }, [getReviewDueData]);

  useEffect(() => {
    init();
  }, [init]);

  /*********
   * MODAL *
   *********/

  const openModal = (type: string) => {
    if (type === '30_day') {
      setModalState({
        isOpenModal: true,
        reviews: reviews30Day,
      });
    } else if (type === '90_day') {
      setModalState({
        isOpenModal: true,
        reviews: reviews90Day,
      });
    }
  };

  const closeModal = () => {
    setModalState({
      isOpenModal: false,
      reviews: initReviews,
    });
  };

  return (
    <>
      {isLoading ? (
        <SkeletonGraph />
      ) : (
        <>
          <Grid container spacing={3} sx={{ p: 3, pt: 0 }}>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" sx={{ ...SUBHEADER_STYLE }}>
                  Due In Next 30 Days
                </Typography>
                <Box>
                  <Button
                    onClick={() => openModal('30_day')}
                    variant="text"
                    size="large"
                    sx={{ ...BUTTON_STYLE }}
                  >
                    {reviews30Day.length}
                  </Button>
                </Box>
                <Box>
                  <Link to={'/admin/performance/review/upcoming'} component={RouterLink as any}>
                    View All
                  </Link>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" sx={{ ...SUBHEADER_STYLE }}>
                  Due in Next 90 Days
                </Typography>
                <Box>
                  <Button
                    onClick={() => openModal('90_day')}
                    variant="text"
                    size="large"
                    sx={{ ...BUTTON_STYLE }}
                  >
                    {reviews90Day.length}
                  </Button>
                </Box>
                <Box>
                  <Link to={'/admin/performance/review/upcoming'} component={RouterLink as any}>
                    View All
                  </Link>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </>
      )}

      <DialogAnimate open={modalState.isOpenModal} onClose={closeModal}>
        <DialogTitle sx={{ mb: 1 }}>Upcoming Reviews</DialogTitle>

        <TableContainer component={Paper} sx={{ p: 2 }}>
          <Table aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Due At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {modalState.reviews.map((row: ReviewDueType) => (
                <TableRow key={row.user_id}>
                  <TableCell>
                    {row.first_name} {row.last_name}
                  </TableCell>
                  <TableCell>{fDateShort(row.next_review_date)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogAnimate>
    </>
  );
}

const SUBHEADER_STYLE = {
  margin: '0',
  lineHeight: '1.5714285714285714',
  fontSize: '0.875rem',
  fontWeight: '400',
  color: '#637381',
  display: 'block',
  marginTop: '4px',
};

const BUTTON_STYLE = {
  width: '90px',
  fontSize: '2rem',
  marginTop: '4px',
  marginBottom: '4px',
};
